import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { MaskedLine, Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import Newsletter from "@/components/Newsletter";

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="border-b border-wire noise-overlay pt-32 pb-20 sm:pt-44 sm:pb-28">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8">
          <p className="font-mono text-[10px] tracking-[0.4em] text-neutral-500">THE STORY — EST. 2026</p>
          <h1 className="mt-6 font-display font-extrabold uppercase leading-[0.95] tracking-tight text-white">
            <MaskedLine delay={0.15} className="text-4xl sm:text-5xl lg:text-6xl">More than clothing.</MaskedLine>
            <MaskedLine delay={0.3} className="text-4xl sm:text-5xl lg:text-6xl">
              <span className="text-neutral-500">It's an outfit.</span>
            </MaskedLine>
          </h1>
        </div>
      </section>

      <section className="border-b border-wire">
        <div className="mx-auto grid max-w-[1600px] grid-cols-1 lg:grid-cols-12">
          <div className="border-b border-wire lg:border-b-0 lg:border-r lg:col-span-7">
            <Reveal className="p-6 sm:p-12">
              <div className="overflow-hidden border border-wire bg-charcoal">
                <img src="/products/back.png" alt="Dejon Richh signature cross artwork on the Classic Tee" className="w-full object-cover" data-testid="about-artwork-image" />
              </div>
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div className="overflow-hidden border border-wire bg-charcoal">
                  <img src="/products/detail.png" alt="DR chest mark detail" className="aspect-[4/3] w-full object-cover" />
                </div>
                <div className="overflow-hidden border border-wire bg-charcoal">
                  <img src="/products/front.png" alt="Classic Dejon Richh Tee front" className="aspect-[4/3] w-full object-cover" />
                </div>
              </div>
            </Reveal>
          </div>
          <div className="flex flex-col justify-center px-6 py-16 sm:px-12 lg:col-span-5 lg:py-28">
            <Reveal>
              <p className="font-mono text-[10px] tracking-[0.35em] text-neutral-500">WHY WE EXIST</p>
              <p className="mt-8 font-serif text-xl sm:text-2xl italic leading-relaxed text-neutral-200" data-testid="about-manifesto">
                "From years of sketches, notes, conversations, and ideas about opening a store, Dejon Richh was created to be more than just clothing. It's about the outfit, the details, the energy, and the way everything comes together."
              </p>
              <p className="mt-10 font-mono text-xs leading-relaxed tracking-wider text-neutral-400">
                Every Dejon Richh piece is designed as part of a complete look — never a standalone afterthought. The DR mark and cross artwork are drawn by hand, distressed on purpose, and printed on premium heavyweight cotton with a vintage-inspired wash.
              </p>
              <p className="mt-6 font-mono text-xs leading-relaxed tracking-wider text-neutral-400">
                No noise. No filler. Just the outfit.
              </p>
              <p className="mt-12 font-script text-2xl text-neutral-200">Dejon Richh</p>
              <p className="mt-2 font-mono text-[10px] tracking-[0.35em] text-neutral-600">FOUNDER — EST. 2026</p>
              <Link to="/shop" data-testid="about-shop-button"
                className="group mt-10 inline-flex items-center gap-3 bg-white px-10 py-4 font-mono text-[11px] tracking-[0.35em] text-black transition-colors hover:bg-neutral-300">
                SHOP THE CLASSIC <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-b border-wire">
        <div className="mx-auto grid max-w-[1600px] grid-cols-1 sm:grid-cols-3 gap-px bg-wire border-b border-wire">
          {[
            { t: "DESIGNED AS OUTFITS", d: "Every piece is built to complete a look — the details, the energy, the way it comes together." },
            { t: "PREMIUM OR NOTHING", d: "Heavyweight cotton, relaxed fits, vintage washes. Quality you feel the first time you put it on." },
            { t: "LIMITED BY DESIGN", d: "Small drops, no restocks promised. When it's gone, it's part of the archive." },
          ].map((v, i) => (
            <div key={v.t} className="bg-obsidian p-10" data-testid={`about-value-${i}`}>
              <p className="font-mono text-[10px] tracking-[0.35em] text-neutral-600">0{i + 1}</p>
              <p className="mt-4 font-display text-lg font-bold uppercase tracking-wide text-white">{v.t}</p>
              <p className="mt-4 font-mono text-[11px] leading-relaxed tracking-wider text-neutral-400">{v.d}</p>
            </div>
          ))}
        </div>
      </section>

      <Marquee items={["SKETCHES", "NOTES", "CONVERSATIONS", "IDEAS", "THE OUTFIT"]} />
      <Newsletter />
    </div>
  );
}
